import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useOrder } from '@hooks/orders';
import { useTranslation } from '@hooks/useTranslation';
import { Toaster } from '@/components/ui/toaster';
import { Page } from '../layouts/Page';
import { MobileIconLink } from '../layouts/MobileButtons';
import { ChevronIcon } from '../icons';
import { OrderDetailsHeader } from './OrderDetailsHeader';
import { OrderHeaderRow } from './OrderHeaderRow';
import type { Order } from './types';
import { CustomerBillToCard } from './CustomerBillToCard';
import { SalespersonSourceCard } from './SalespersonSourceCard';
import { AttioSyncStatus } from './AttioSyncStatus';
import { PaymentCard } from './PaymentCard';
import { OrderYieldCard } from './OrderYieldCard';
import { EventsSectionCard } from './EventsSectionCard';
import { ExtractionLoadingCard, ExtractionFailedNotice, ExtractionNotesCard } from './ExtractionLoadingCard';
import { WhatChangedCard } from './WhatChangedCard';
import { CreateEventDialog } from './CreateEvent';
import { FrontEmailLink } from './FrontEmailLink';
import type { CurrentUser } from '~/react/lib/capabilities';

interface OrderDetailsProps {
  orderId: number;
  initialOrder?: Order;
  currentUser?: CurrentUser;
}

export default function OrderDetails({ orderId, initialOrder, currentUser }: OrderDetailsProps) {
  const { t } = useTranslation();
  const {
    data: order,
    isLoading,
    error,
    extractionTimedOut,
    attioSyncTimedOut,
  } = useOrder(orderId, { initialData: initialOrder });
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [collapsedEventIds, setCollapsedEventIds] = useState<number[]>([]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <p className="font-inter text-sm text-grey">{t('loading')}</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <p className="font-inter text-sm text-chilli-red">
          {error instanceof Error ? error.message : t('error')}
        </p>
      </div>
    );
  }

  const isCancelled = order.order_status === 'cancelled';

  return (
    <Page>
      <Page.Header className="py-3 md:py-0">
        <OrderHeaderRow
          order={order}
          headingAs="h1"
          className="px-6 md:hidden"
          leading={
            <MobileIconLink href="/orders" aria-label={t('header.breadcrumb_orders')} className="size-14">
              <ChevronIcon direction="left" />
            </MobileIconLink>
          }
        />
        <div className="hidden md:flex w-full px-6 sm:px-8 lg:px-16">
          <OrderDetailsHeader
            orderId={orderId}
            orderNumber={order.reference_number}
            status={order.order_status}
            frontConversationId={order.front_conversation_id}
          />
        </div>
      </Page.Header>
      <Page.Body>
        <div data-testid="order-details-body" className={cn('w-full px-6 sm:px-8 lg:px-16 space-y-8 pb-4 md:pb-0', isCancelled && 'opacity-50')}>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <h2 className="font-kapra-expanded text-2xl font-black uppercase text-off-black">
                {t('order_section_title')}
              </h2>
              <span className="md:hidden">
                <FrontEmailLink conversationId={order.front_conversation_id} />
              </span>
            </div>

            <div className="space-y-8 rounded-input border border-dinnerplate-white bg-off-white p-6">
              <CustomerBillToCard
                orderId={orderId}
                canGenerateDocumentsDirectly={order.documents_directly_generatable}
                contact={order.contact}
                organisation={order.organisation}
                billing={order.billing}
                draftEvents={order.draft_events}
                hasPublishableEvents={order.has_publishable_events}
                orderStatus={order.order_status}
                xeroInvoiceId={order.xero_invoice_id}
                currentUser={currentUser}
              />

              <SalespersonSourceCard
                salesperson={order.salesperson}
                orderSource={order.order_source}
                paymentBadge={
                  <PaymentCard
                    paymentStatus={order.payment_status}
                    stripeChargeUrl={order.stripe_charge_url}
                  />
                }
                referredBy={order.referred_by}
              />

              {/* Attio takes this row's right column. Rendered whenever either
                  half has something to say -- an order with no yield figures
                  must still show the Attio row. */}
              {(order.yield_data || order.attio) && (
                <OrderYieldCard
                  attio={
                    <AttioSyncStatus
                      attio={order.attio}
                      attioSyncTimedOut={attioSyncTimedOut}
                      currentUser={currentUser}
                      orderId={orderId}
                    />
                  }
                  yieldData={order.yield_data}
                />
              )}
            </div>
          </div>

          {order.extraction_status === 'processing' && !extractionTimedOut ? (
            <ExtractionLoadingCard />
          ) : (
            <>
              {/* Notes carry the specifics (unmatched dishes, failure reason); the
                  static notice is the fallback when a failure left none (e.g. the
                  poll timed out before the server wrote anything). */}
              {order.extraction_notes ? (
                <ExtractionNotesCard
                  notes={order.extraction_notes}
                  conversationId={order.front_conversation_id}
                />
              ) : (
                (order.extraction_status === 'failed' || extractionTimedOut) && (
                  <ExtractionFailedNotice />
                )
              )}
              {order.change_log && order.change_log.changes.length > 0 && (
                <WhatChangedCard changeLog={order.change_log} />
              )}
              <EventsSectionCard
                orderId={orderId}
                currentUser={currentUser}
                onAddEvents={() => setIsCreateEventOpen(true)}
                actionsDisabled={isCancelled}
                collapsedEventIds={collapsedEventIds}
              />
            </>
          )}

          <CreateEventDialog
            isOpen={isCreateEventOpen}
            onOpenChange={setIsCreateEventOpen}
            orderId={orderId}
            onEventsCreated={setCollapsedEventIds}
          />
        </div>
      </Page.Body>
      <Toaster />
    </Page>
  );
}
